import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  persistActiveRunId,
  readActiveRunId,
  safetyStockService,
  type SafetyStockRunStatus,
} from '@/services/safetyStockService';

export type SafetyStockApplyModalMode = 'confirm' | 'progress';

const TERMINAL = new Set(['SUCCESS', 'PARTIAL_SUCCESS', 'FAILED', 'CANCELLED']);
const POLL_MS = 2000;
const MAX_POLLS = 900;

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function useSafetyStockApplyFlow(options?: {
  warehouseId?: number | null;
  onComplete?: (run: SafetyStockRunStatus) => void;
}) {
  const { t } = useTranslation();
  const warehouseId = options?.warehouseId ?? null;
  const onCompleteRef = useRef(options?.onComplete);
  onCompleteRef.current = options?.onComplete;

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<SafetyStockApplyModalMode>('confirm');
  const [isRunning, setIsRunning] = useState(false);
  const [flowComplete, setFlowComplete] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [runStatus, setRunStatus] = useState<SafetyStockRunStatus | null>(null);
  const [reattached, setReattached] = useState(false);
  const [confirmItemCount, setConfirmItemCount] = useState<number | null>(null);
  const abortRef = useRef(false);

  const closeModal = useCallback(() => {
    // Allow dismiss while running: poll continues in background (run_id in localStorage).
    setModalOpen(false);
    if (isRunning) return;
    setModalMode('confirm');
    setFlowComplete(false);
    setErrorMessage(null);
    setReattached(false);
    if (flowComplete || (runStatus && TERMINAL.has(runStatus.status))) {
      persistActiveRunId(null);
    }
  }, [isRunning, flowComplete, runStatus]);

  const reopenProgress = useCallback(() => {
    if (!isRunning && !flowComplete && !errorMessage) return;
    setModalMode('progress');
    setModalOpen(true);
  }, [isRunning, flowComplete, errorMessage]);

  const requestApply = useCallback((itemCount?: number | null) => {
    setConfirmItemCount(itemCount ?? null);
    setModalMode('confirm');
    setFlowComplete(false);
    setErrorMessage(null);
    setReattached(false);
    setRunStatus(null);
    setModalOpen(true);
  }, []);

  const pollUntilDone = useCallback(async (runId: string) => {
    persistActiveRunId(runId);
    setIsRunning(true);
    setModalMode('progress');
    setErrorMessage(null);
    setFlowComplete(false);

    for (let i = 0; i < MAX_POLLS; i += 1) {
      if (abortRef.current) return;
      try {
        const status = await safetyStockService.getRun(runId);
        setRunStatus(status);
        if (TERMINAL.has(status.status)) {
          setIsRunning(false);
          if (status.status === 'FAILED' || status.status === 'CANCELLED') {
            setErrorMessage(
              status.error_message ||
                t('safetyStock.apply.errors.runFailed'),
            );
            persistActiveRunId(null);
            return;
          }
          // Wait briefly for summary if still building
          if (!status.summary_ready && status.summary_building) {
            for (let j = 0; j < 30; j += 1) {
              await sleep(POLL_MS);
              const again = await safetyStockService.getRun(runId);
              setRunStatus(again);
              if (again.summary_ready || !again.summary_building) break;
            }
          }
          setFlowComplete(true);
          persistActiveRunId(null);
          onCompleteRef.current?.(status);
          return;
        }
      } catch (err) {
        const message =
          err && typeof err === 'object' && 'message' in err
            ? String((err as { message: string }).message)
            : t('safetyStock.apply.errors.pollFailed');
        setErrorMessage(message);
        setIsRunning(false);
        return;
      }
      await sleep(POLL_MS);
    }
    setErrorMessage(t('safetyStock.apply.errors.timeout'));
    setIsRunning(false);
  }, [t]);

  const confirmApply = useCallback(async () => {
    if (typeof warehouseId !== 'number') {
      setErrorMessage(t('safetyStock.selectWarehouse'));
      setIsRunning(false);
      return;
    }
    setIsRunning(true);
    setModalMode('progress');
    setErrorMessage(null);
    setFlowComplete(false);
    setReattached(false);
    setRunStatus({
      run_id: '',
      organization_id: 0,
      status: 'RUNNING',
      total_items: confirmItemCount ?? 0,
      successful_items: 0,
      failed_items: 0,
      started_at: new Date().toISOString(),
      summary_ready: false,
      summary_building: false,
    });

    const result = await safetyStockService.startOrgRecalculate(warehouseId);
    if (result.kind === 'accepted') {
      await pollUntilDone(result.data.run_id);
      return;
    }
    if (result.kind === 'reattach') {
      setReattached(true);
      await pollUntilDone(result.data.run_id);
      return;
    }
    if (result.kind === 'forbidden') {
      setErrorMessage(t('safetyStock.apply.errors.applyDisabled'));
      setIsRunning(false);
      return;
    }
    setErrorMessage(result.message || t('safetyStock.apply.errors.startFailed'));
    setIsRunning(false);
  }, [pollUntilDone, t, confirmItemCount, warehouseId]);

  const retryApply = useCallback(async () => {
    setErrorMessage(null);
    setFlowComplete(false);
    await confirmApply();
  }, [confirmApply]);

  // Resume after page reload if a run is still active.
  useEffect(() => {
    abortRef.current = false;
    const stored = readActiveRunId();
    if (!stored) return;

    let cancelled = false;
    (async () => {
      try {
        const status = await safetyStockService.getRun(stored);
        if (cancelled) return;
        if (TERMINAL.has(status.status)) {
          persistActiveRunId(null);
          return;
        }
        setRunStatus(status);
        setModalOpen(true);
        setModalMode('progress');
        setReattached(true);
        await pollUntilDone(stored);
      } catch {
        persistActiveRunId(null);
      }
    })();

    return () => {
      cancelled = true;
      abortRef.current = true;
    };
  }, [pollUntilDone]);

  return {
    modalOpen,
    modalMode,
    isRunning,
    flowComplete,
    errorMessage,
    runStatus,
    reattached,
    confirmItemCount,
    requestApply,
    confirmApply,
    retryApply,
    closeModal,
    reopenProgress,
  };
}
